import {
  Pill,
  Stethoscope,
  Smile,
  Wifi,
  GraduationCap,
  Languages,
  Car,
  Building2,
  Monitor,
  ShoppingCart,
  HeartPulse,
  School,
  type LucideIcon,
} from "lucide-react";


export type SegmentCase = {
  slug: string;
  name: string;
  shortName: string;
  category: "Saúde" | "Educação" | "Varejo" | "Serviço";
  icon: LucideIcon;
  tagline: string;
  challenges: string;
  solutions: string;
  metrics: { value: string; label: string }[];
  testimonials: { quote: string; author: string; role: string }[];
};

export const SEGMENT_CASES: SegmentCase[] = [
  {
    slug: "farmacias",
    name: "Farmácias",
    shortName: "Farmácias",
    category: "Saúde",
    icon: Pill,
    tagline: "Organize pedidos, dúvidas de estoque e recompra sem perder cliente na fila.",
    challenges:
      "Farmácias enfrentam alto volume de atendimento simultâneo, especialmente em horários de pico. Demandas por medicamentos comuns e controlados chegam por telefone, WhatsApp e balcão, dificultando a organização. Problemas recorrentes incluem perda de pedidos, lentidão para responder dúvidas sobre disponibilidade de produtos e filas de espera excessivas.",
    solutions:
      "A Kentro implementou uma central de atendimento unificada que integra WhatsApp Business, telefone e chatbot para organizar a fila de pedidos. O CRM passou a registrar históricos de compras, preferências e lembretes para receitas recorrentes. O assistente de IA opera 24h, verifica disponibilidade de produtos, registra leads, diferencia curiosos de interessados e, em farmácias mais avançadas, envia preços e links de pagamento automaticamente.",
    metrics: [
      { value: "-45%", label: "no tempo médio de atendimento por canal" },
      { value: "+32%", label: "na recompra de clientes em até 30 dias" },
      { value: "-60%", label: "nas reclamações por demora de resposta" },
      { value: "+28%", label: "em atendimentos fora do horário comercial" },
      { value: "-35%", label: "em perguntas repetitivas para a equipe" },
    ],
    testimonials: [
      {
        quote:
          "Antes, perdíamos clientes por não conseguir responder rápido. Hoje, com o painel da Kentro, atendemos tudo em segundos.",
        author: "Carlos Azevedo",
        role: "Farmácia Boa Vida",
      },
      {
        quote:
          "Conseguimos montar uma campanha de lembretes automáticos para clientes com receitas contínuas. Isso fidelizou muitos deles.",
        author: "Renata Lima",
        role: "Farmacêutica e gestora",
      },
      {
        quote:
          "O assistente de IA virou nosso plantonista noturno. Tira dúvidas, sugere produtos e até fecha vendas. E os clientes gostam de falar com ele.",
        author: "Lídia Vasconcelos",
        role: "Gerente — Farmácia Popular Mais Vida",
      },
    ],
  },
  {
    slug: "clinicas-medicas",
    name: "Clínicas Médicas",
    shortName: "Clínicas Médicas",
    category: "Saúde",
    icon: Stethoscope,
    tagline: "Acabe com o no-show, organize a agenda e responda no WhatsApp em segundos.",
    challenges:
      "Clínicas médicas sofrem com agendamentos descentralizados, grande número de faltas, dúvidas repetitivas sobre exames, horários e convênios, e alta demanda nos horários de pico. Recepções ficam sobrecarregadas e pacientes enfrentam longas esperas no WhatsApp ou telefone.",
    solutions:
      "A Kentro integrou WhatsApp, telefone e Instagram em um único painel. O assistente de IA atua fora do horário e nos picos, respondendo dúvidas frequentes, orientando sobre preparo de exames, agendando consultas no CRM e enviando confirmações automáticas. Também classifica leads entre curiosos e interessados, priorizando o atendimento comercial e reduzindo o no-show.",
    metrics: [
      { value: "-40%", label: "nas faltas a consultas (no-show)" },
      { value: "+30%", label: "em agendamentos pelo WhatsApp" },
      { value: "-50%", label: "no tempo de resposta em horários de pico" },
      { value: "38%", label: "do volume vem de fora do horário comercial" },
      { value: "-45%", label: "nas ligações para a recepção" },
    ],
    testimonials: [
      {
        quote:
          "Antes, nossa recepção vivia lotada de mensagens e ligações. Agora, tudo está centralizado, e o assistente da Kentro cuida das perguntas mais comuns.",
        author: "Juliana Amaral",
        role: "Gestora — Clínica VivaMais",
      },
      {
        quote:
          "O índice de faltas despencou depois que os lembretes automáticos começaram a ser enviados. E o melhor: sem esforço da equipe.",
        author: "Dr. Rafael Toledo",
        role: "Clínico geral",
      },
      {
        quote:
          "O assistente explica preparos de exames e classifica quem quer só saber preço de quem realmente vai agendar. Otimizou muito nosso trabalho.",
        author: "André Mendonça",
        role: "Coordenador comercial",
      },
    ],
  },
  {
    slug: "clinicas-odontologicas",
    name: "Clínicas Odontológicas",
    shortName: "Clínicas Odontológicas",
    category: "Saúde",
    icon: Smile,
    tagline: "Menos faltas, mais orçamentos fechados e agenda cheia até fora do expediente.",
    challenges:
      "Clínicas odontológicas enfrentam alta taxa de faltas, agendamentos desorganizados, dificuldade em confirmar horários, dúvidas frequentes sobre valores e procedimentos, além de tempo ocioso entre atendimentos por desistências e desmarques.",
    solutions:
      "A Kentro centralizou o atendimento em um painel omnichannel e implantou o assistente de IA para pré-triagem, perguntas frequentes sobre tratamentos, planos e preços, lembretes automáticos e agendamento em tempo real. O CRM separa leads prontos para iniciar tratamento dos apenas curiosos. Em clínicas mais avançadas, o assistente envia links de pagamento antecipado para garantir comparecimento.",
    metrics: [
      { value: "-48%", label: "nas faltas a consultas odontológicas" },
      { value: "+36%", label: "em agendamentos confirmados via WhatsApp" },
      { value: "-43%", label: "em dúvidas frequentes respondidas manualmente" },
      { value: "29%", label: "de novos agendamentos vêm fora do horário" },
      { value: "+22%", label: "na conversão de orçamentos enviados pela IA" },
    ],
    testimonials: [
      {
        quote:
          "Com a Kentro, paramos de perder pacientes por esquecimentos. Os lembretes automáticos foram um divisor de águas.",
        author: "Dr. Caio Martins",
        role: "Odontologista — Clínica Sorrir Mais",
      },
      {
        quote:
          "Conseguimos receber pagamentos via link antes da consulta. Isso reduziu drasticamente as desistências de última hora.",
        author: "Renata Carvalho",
        role: "Gerente financeira",
      },
      {
        quote:
          "Antes tínhamos muita ociosidade na agenda. Agora o sistema preenche horários vagos com novos agendamentos, inclusive fora do expediente.",
        author: "Lucas Nogueira",
        role: "Diretor administrativo",
      },
    ],
  },
  {
    slug: "provedores-de-internet",
    name: "Provedores de Internet",
    shortName: "Provedores (ISP)",
    category: "Serviço",
    icon: Wifi,
    tagline: "Triagem técnica automática, suporte 24h e leads quentes para o comercial.",
    challenges:
      "Provedores regionais lidam com alto volume de chamados técnicos, muitos repetitivos ou de falhas comuns. Há dificuldade em manter histórico do cliente entre canais e pouca previsibilidade de picos. Também é comum perder leads por triagem inadequada ou resposta lenta fora do horário.",
    solutions:
      "A Kentro integrou WhatsApp, telefone, Instagram e e-mail em um painel único. O assistente de IA faz triagem inicial, identifica tipo de problema, urgência e perfil do cliente, registra leads no CRM, sugere planos e envia links de cobrança. Fora do horário, segue ativo prestando suporte básico — reinicialização de equipamentos e acompanhamento de protocolos.",
    metrics: [
      { value: "-58%", label: "na sobrecarga do suporte humano" },
      { value: "+42%", label: "na resolutividade no primeiro contato" },
      { value: "+35%", label: "na recuperação de leads interessados" },
      { value: "76%", label: "dos provedores rodam atendimento 24h" },
      { value: "-50%", label: "no tempo de resposta em horários de pico" },
    ],
    testimonials: [
      {
        quote:
          "Antes, nossos atendentes passavam horas com problemas básicos. Agora, o assistente faz o primeiro filtro e nos envia só o que precisa de gente.",
        author: "Tiago Vilar",
        role: "Gerente — NetCidade",
      },
      {
        quote:
          "Conseguimos atender fora do horário e isso fidelizou clientes que antes cancelavam.",
        author: "Juliana Barreto",
        role: "Proprietária — ConectaMais",
      },
      {
        quote:
          "O assistente de IA virou aliado para captar novos clientes e até fechar planos diretamente com quem está pronto.",
        author: "Bruno Matos",
        role: "Comercial — LinkMax",
      },
    ],
  },
  {
    slug: "faculdades",
    name: "Faculdades",
    shortName: "Faculdades",
    category: "Educação",
    icon: GraduationCap,
    tagline: "Mais matrículas no digital, leads acompanhados e zero candidato perdido.",
    challenges:
      "Faculdades têm dificuldade em manter comunicação fluida em períodos de matrícula e vestibular. Há excesso de dúvidas sobre cursos, ingresso, financiamento, documentos e prazos. Equipes ficam sobrecarregadas com ligações, e-mails e redes sociais, e a perda de leads por demora no retorno é comum.",
    solutions:
      "A Kentro integrou WhatsApp, telefone, Instagram e site em um atendimento centralizado. O assistente de IA atua em todo o funil de captação, inscrição e matrícula — responde dúvidas, direciona para o curso correto, coleta dados e registra interações no CRM. Fora do horário, mantém suporte ativo, garantindo presença constante da instituição.",
    metrics: [
      { value: "+46%", label: "em matrículas pelos canais digitais" },
      { value: "-38%", label: "no tempo de resposta durante o vestibular" },
      { value: "+29%", label: "em conversão de leads qualificados pela IA" },
      { value: "36%", label: "das interações ocorrem fora do horário" },
      { value: "-41%", label: "na sobrecarga de chamadas e e-mails" },
    ],
    testimonials: [
      {
        quote:
          "Com a Kentro, conseguimos responder mais rápido, orientar melhor e converter muito mais candidatos em alunos.",
        author: "Daniela Teles",
        role: "Coordenadora de captação — Faculdade AlfaSul",
      },
      {
        quote:
          "O assistente virou parte do nosso time. Não só atende, como classifica os leads e agiliza o trabalho do comercial.",
        author: "João Marcos",
        role: "Gerente de marketing",
      },
      {
        quote:
          "Antes, muitos leads sumiam porque demorávamos para responder. Agora aproveitamos cada oportunidade.",
        author: "Victor Lima",
        role: "Diretor comercial",
      },
    ],
  },
  {
    slug: "cursos-de-idiomas",
    name: "Cursos de Idiomas",
    shortName: "Cursos de Idiomas",
    category: "Educação",
    icon: Languages,
    tagline: "Menos evasão, mais matrícula e aula experimental agendada sozinha.",
    challenges:
      "Escolas de idiomas enfrentam rotatividade, dificuldade na conversão de interessados, esquecimento de aulas e baixa adesão a atividades extracurriculares. Excesso de dúvidas repetidas sobre níveis, horários e modalidades sobrecarregam o atendimento.",
    solutions:
      "A Kentro centralizou WhatsApp, telefone, redes sociais e formulários. O assistente de IA responde dúvidas comuns, apresenta cursos, agenda aulas experimentais, envia lembretes e registra a jornada no CRM. Também atua em horários alternativos, convertendo leads fora do expediente tradicional.",
    metrics: [
      { value: "-49%", label: "na evasão por esquecimento de aula" },
      { value: "+35%", label: "na conversão de interessados em alunos" },
      { value: "-44%", label: "em dúvidas operacionais manuais" },
      { value: "31%", label: "das matrículas vêm fora do horário" },
      { value: "+26%", label: "em adesão a eventos e oficinas" },
    ],
    testimonials: [
      {
        quote:
          "Antes a gente perdia muitos alunos por falta de acompanhamento. Agora o CRM e o assistente fazem isso por nós.",
        author: "Carla Mendes",
        role: "Diretora — Idioma Global",
      },
      {
        quote:
          "O agendamento de aula experimental automatizado facilitou muito nosso processo de conversão.",
        author: "Roberto Souza",
        role: "Consultor educacional",
      },
      {
        quote:
          "Atendimento à noite e aos sábados nos aproxima de quem trabalha o dia inteiro.",
        author: "Lucas Fernandes",
        role: "Atendimento e vendas",
      },
    ],
  },
  {
    slug: "concessionarias",
    name: "Concessionárias",
    shortName: "Concessionárias",
    category: "Varejo",
    icon: Car,
    tagline: "Lead de alto valor não esfria mais — test drive agendado em minutos.",
    challenges:
      "Concessionárias precisam acompanhar de perto leads de alto valor, responder rápido sobre veículos, agendar test drives e gerir propostas. Muitos leads esfriam por falta de retorno, e o volume por WhatsApp e redes sociais dificulta o controle manual do funil.",
    solutions:
      "A Kentro integrou todos os canais digitais em uma central única. O assistente de IA responde sobre modelos, preços, financiamento e promoções, coleta dados, agenda test drives e aciona o comercial em tempo real quando o lead está pronto. O CRM mostra toda a jornada, facilitando follow-up e personalização.",
    metrics: [
      { value: "+51%", label: "na conversão de leads em propostas" },
      { value: "-44%", label: "no tempo de resposta no WhatsApp" },
      { value: "+36%", label: "em test drives agendados automaticamente" },
      { value: "32%", label: "dos agendamentos vêm fora do horário" },
      { value: "+29%", label: "na recompra de clientes no CRM" },
    ],
    testimonials: [
      {
        quote:
          "Hoje o lead cai no WhatsApp e em minutos o assistente já deu as primeiras respostas. Quando o vendedor assume, metade do trabalho já está feito.",
        author: "Adriana Vilela",
        role: "Gerente — AutoCenter Premium",
      },
      {
        quote:
          "A integração com o CRM nos permite personalizar o atendimento como nunca. Sabemos o que o cliente consultou e o que precisa.",
        author: "Lúcio Ramos",
        role: "Coordenador comercial",
      },
      {
        quote:
          "Já fechamos venda com cliente atendido pela IA às 23h. Isso seria impossível antes da Kentro.",
        author: "Patrícia Rocha",
        role: "Diretora executiva",
      },
    ],
  },
  {
    slug: "condominios",
    name: "Condomínios",
    shortName: "Condomínios",
    category: "Serviço",
    icon: Building2,
    tagline: "Boletos, ocorrências e reservas resolvidos sozinhos, com gente só no que importa.",
    challenges:
      "Gestoras de condomínio enfrentam demandas simultâneas de moradores e síndicos. Há dificuldade na comunicação centralizada, excesso de ligações e e-mails, solicitações de segunda via, agendamento de áreas comuns e acompanhamento de ocorrências e manutenções.",
    solutions:
      "A Kentro implantou um sistema omnichannel que concentra todas as interações em um painel único. O assistente de IA opera fora do horário, atende dúvidas frequentes, emite segunda via via integração com ERP e filtra demandas por prioridade. Integrações com sistemas de gestão registram ocorrências, atualizam status de manutenção e fazem triagens sem envolvimento humano.",
    metrics: [
      { value: "-52%", label: "no volume de ligações para a central" },
      { value: "+40%", label: "na resolução no primeiro contato" },
      { value: "-47%", label: "no tempo de resposta para demandas comuns" },
      { value: "34%", label: "das interações ocorrem fora do horário" },
      { value: "18h/mês", label: "economizadas por atendente em triagem" },
    ],
    testimonials: [
      {
        quote:
          "O assistente da Kentro resolveu nosso gargalo. Os condôminos conseguem resolver sozinhos o que antes lotava nossa linha.",
        author: "Diego Martins",
        role: "Diretor — ConectaCon",
      },
      {
        quote:
          "Automatizamos o envio de boletos e o registro de ocorrências. A redução na carga da equipe foi visível já no primeiro mês.",
        author: "Mariana Prado",
        role: "Gerente operacional",
      },
      {
        quote:
          "O assistente de IA é educado, eficiente e resolve tudo. Os moradores elogiam a agilidade.",
        author: "Laura Reis",
        role: "Síndica profissional",
      },
    ],
  },
  {
    slug: "lojas-de-informatica",
    name: "Lojas de Informática",
    shortName: "Lojas de Informática",
    category: "Varejo",
    icon: Monitor,
    tagline: "Orçamento técnico em segundos e venda fechada até de madrugada.",
    challenges:
      "Lojas de informática lidam com alto volume de consultas técnicas, pedidos de orçamento, dúvidas sobre compatibilidade de peças e suporte pós-venda. Atendimentos são abandonados por demora ou falta de clareza, e há perda de vendas pela dificuldade de classificar leads com real intenção de compra.",
    solutions:
      "A Kentro unificou WhatsApp, redes sociais e canais de suporte. O assistente de IA responde dúvidas sobre produtos, indica compatibilidades, sugere itens complementares, registra orçamentos e encaminha leads ao comercial. Fora do horário, oferece suporte básico e agenda atendimento técnico. O CRM classifica o lead e acompanha o histórico do cliente com precisão.",
    metrics: [
      { value: "-47%", label: "no tempo de resposta em canais digitais" },
      { value: "+36%", label: "na conversão de orçamentos pela IA" },
      { value: "+42%", label: "em vendas fechadas fora do horário" },
      { value: "-40%", label: "em atendimentos abandonados" },
      { value: "+31%", label: "na recompra de clientes no CRM" },
    ],
    testimonials: [
      {
        quote:
          "O assistente responde até perguntas técnicas, o que ajuda quem está comparando peças. Aumentamos nossas vendas com ele.",
        author: "Rafael Batista",
        role: "Gerente — TecnoShop",
      },
      {
        quote:
          "Muitos clientes compram de madrugada. Com o assistente ativo, garantimos atendimento e fechamentos a qualquer hora.",
        author: "Eduardo Silva",
        role: "Proprietário",
      },
      {
        quote:
          "O tempo de resposta caiu pela metade. Isso melhorou a experiência e aumentou nosso NPS.",
        author: "Leandro Teles",
        role: "Atendimento técnico",
      },
    ],
  },
  {
    slug: "ecommerce",
    name: "E-commerce",
    shortName: "E-commerce",
    category: "Varejo",
    icon: ShoppingCart,
    tagline: "Recupere carrinho abandonado e responda dúvida de compra em segundos, 24h por dia.",
    challenges:
      "Lojas online recebem dúvidas sobre frete, prazo, estoque, troca e rastreio em vários canais ao mesmo tempo — WhatsApp, Instagram, e-mail e chat do site. A maior parte das perdas acontece no intervalo entre a dúvida e a resposta: o cliente abandona o carrinho, compra no concorrente ou abre reclamação por falta de informação sobre o pedido. No pós-venda, a equipe gasta horas respondendo \"onde está meu pedido?\" e perde tempo de venda.",
    solutions:
      "A Kentro unifica WhatsApp Oficial, Instagram, Facebook, e-mail e chat do site em uma única fila com histórico completo do cliente. O assistente de IA responde dúvidas de produto, frete e prazo, consulta status de pedido, envia link de pagamento e reengaja carrinho abandonado com mensagens automáticas. Campanhas segmentadas no CRM reativam clientes inativos e promovem recompra, enquanto o time humano assume apenas os casos que realmente precisam de negociação.",
    metrics: [
      { value: "+34%", label: "na recuperação de carrinhos abandonados" },
      { value: "-52%", label: "no tempo de primeira resposta" },
      { value: "+41%", label: "em vendas fechadas fora do horário comercial" },
      { value: "-38%", label: "em tickets de \"onde está meu pedido\"" },
      { value: "+29%", label: "na taxa de recompra em 60 dias" },
    ],
    testimonials: [
      {
        quote:
          "A IA responde frete, prazo e estoque na hora. Nossa taxa de abandono caiu de forma visível no primeiro mês.",
        author: "Bruna Salgado",
        role: "Gerente de e-commerce",
      },
      {
        quote:
          "Antes tínhamos três caixas de entrada diferentes. Hoje é uma fila só, com histórico do cliente na tela.",
        author: "Diego Martins",
        role: "Coordenador de atendimento",
      },
      {
        quote:
          "As campanhas de recompra pelo WhatsApp viraram nosso canal mais rentável. Sai mais barato que anúncio.",
        author: "Priscila Andrade",
        role: "Proprietária — loja de moda online",
      },
    ],
  },
  {
    slug: "clinicas",
    name: "Clínicas",
    shortName: "Clínicas",
    category: "Saúde",
    icon: HeartPulse,
    tagline: "Agenda cheia, menos falta e confirmação automática em clínicas médicas, odonto e estética.",
    challenges:
      "Clínicas de qualquer especialidade convivem com o mesmo trio de problemas: agenda com buracos, alto índice de faltas e recepção sobrecarregada. As solicitações de agendamento chegam por WhatsApp, telefone, Instagram e site, e quando ninguém responde em minutos o paciente marca em outro lugar. Reagendamentos manuais, confirmação por ligação e falta de histórico do paciente consomem o tempo da equipe que deveria estar cuidando de quem está na clínica.",
    solutions:
      "A Kentro centraliza todos os canais em um só painel, com IA treinada para triagem, informação de convênios, valores e preparo de exames ou procedimentos. Confirmações e lembretes automáticos reduzem o no-show, e a lista de espera é acionada assim que um horário abre. O CRM guarda o histórico completo do paciente, dispara retorno e recall de manutenção e mede a conversão de cada canal — respeitando as exigências de sigilo e a LGPD no tratamento de dados sensíveis.",
    metrics: [
      { value: "-43%", label: "no índice de faltas (no-show)" },
      { value: "+37%", label: "em agendamentos confirmados por canal digital" },
      { value: "-55%", label: "no tempo de resposta na recepção" },
      { value: "+31%", label: "no retorno e recall de pacientes" },
      { value: "+26%", label: "em agendamentos fora do horário de expediente" },
    ],
    testimonials: [
      {
        quote:
          "Os lembretes automáticos praticamente acabaram com o buraco na agenda das terças e quintas.",
        author: "Dra. Marina Peixoto",
        role: "Clínica multidisciplinar",
      },
      {
        quote:
          "A recepção parou de atender três telas ao mesmo tempo. O atendimento presencial melhorou junto.",
        author: "Camila Duarte",
        role: "Gestora de clínica odontológica",
      },
      {
        quote:
          "A IA explica preparo, valores e convênio sem errar. Só chega pra gente o que precisa de decisão humana.",
        author: "Rodrigo Nunes",
        role: "Diretor — clínica de estética",
      },
    ],
  },
  {
    slug: "escolas-e-faculdades",
    name: "Escolas e Faculdades",
    shortName: "Escolas e Faculdades",
    category: "Educação",
    icon: School,
    tagline: "Da captação à rematrícula: atendimento organizado para toda a jornada do aluno.",
    challenges:
      "Instituições de ensino vivem picos sazonais de matrícula e rematrícula em que o volume de contatos explode. Dúvidas sobre valores, bolsas, documentação, transporte, calendário e financeiro chegam por WhatsApp, telefone e redes sociais, misturando pais, alunos e interessados na mesma caixa. Sem fila organizada e sem histórico, leads de matrícula esfriam, o financeiro perde controle de inadimplência e a secretaria repete as mesmas respostas dezenas de vezes por dia.",
    solutions:
      "A Kentro organiza secretaria, comercial, financeiro e coordenação em filas separadas dentro de um único painel, com histórico unificado por família. O assistente de IA responde 24h sobre valores, documentos, bolsas e prazos, qualifica o interessado e agenda visita ou entrevista. Campanhas automáticas conduzem a rematrícula, avisos de calendário e lembretes de pendência financeira, e os relatórios mostram exatamente quantas matrículas cada canal gerou.",
    metrics: [
      { value: "+39%", label: "na conversão de leads em matrícula" },
      { value: "-49%", label: "no tempo de resposta da secretaria" },
      { value: "+33%", label: "na taxa de rematrícula com campanhas automáticas" },
      { value: "-36%", label: "em perguntas repetitivas para a equipe" },
      { value: "+27%", label: "em contatos atendidos no pico de matrículas" },
    ],
    testimonials: [
      {
        quote:
          "No período de matrícula o volume triplica. Com a fila organizada, nenhum interessado ficou sem resposta.",
        author: "Fernanda Coelho",
        role: "Coordenadora de secretaria",
      },
      {
        quote:
          "A IA responde valores, bolsa e documentação a qualquer hora. Muitos pais entram em contato à noite.",
        author: "Marcelo Andrade",
        role: "Diretor administrativo",
      },
      {
        quote:
          "As campanhas de rematrícula pelo WhatsApp reduziram muito o trabalho manual de ligação.",
        author: "Juliana Prado",
        role: "Gestora acadêmica",
      },
    ],
  },
];

export function getSegmentBySlug(slug: string) {
  return SEGMENT_CASES.find((s) => s.slug === slug);
}
